import "../../components/ProfileCard/ProfileCard.scss";
import "./Stats.scss";
import {CSSProperties, ReactNode, useEffect, useState} from "react";
import axios from "axios";
import {PATHS_API} from "../../../../../enum";
import PageLoader from "../../../../shared/PageLoader/PageLoader";
import {ClockIcon, DistanceIcon, RatioIcon, SkullIcon} from "../../../StatsPage/StatsArt";
import Survivor, {SurvivorZone, ZoneId} from "./SurvivorArt";
import survivorImg from "../../../../assets/stats-survivor.png";

interface IWeapon { name: string, hits: number, kills: number, damage: number, longestKill: number, zones: Record<string, number> }
interface IServerStats {
    name: string,
    kills: { players: number, infected: number, animals: number },
    deaths: number, suicides: number, kdratio: number,
    longestKill: number, longestShot: number, distanceTraveled: number, hits: number,
    shots: { fired: number, hit: number, hitPlayers: number, hitInfected: number, hitAnimals: number, hitVehicles: number },
    zones: Record<string, number>,
    weapons: IWeapon[],
}
interface IServer { key: string, name: string, stats: IServerStats | null, unavailable?: boolean }

// Частини тіла: CFTools віддає brain окремо від head, у списку об'єднуємо їх як «Голова»
const ZONES: { id: ZoneId, label: string, short: string, keys: string[] }[] = [
    { id: "head", label: "Голова", short: "Голова", keys: ["head", "brain"] },
    { id: "torso", label: "Тулуб", short: "Тулуб", keys: ["torso"] },
    { id: "leftarm", label: "Ліва рука", short: "Л. рука", keys: ["leftarm"] },
    { id: "rightarm", label: "Права рука", short: "П. рука", keys: ["rightarm"] },
    { id: "lefthand", label: "Ліва кисть", short: "Л. кисть", keys: ["lefthand"] },
    { id: "righthand", label: "Права кисть", short: "П. кисть", keys: ["righthand"] },
    { id: "leftleg", label: "Ліва нога", short: "Л. нога", keys: ["leftleg"] },
    { id: "rightleg", label: "Права нога", short: "П. нога", keys: ["rightleg"] },
    { id: "leftfoot", label: "Ліва стопа", short: "Л. стопа", keys: ["leftfoot"] },
    { id: "rightfoot", label: "Права стопа", short: "П. стопа", keys: ["rightfoot"] },
];

const WEAPON_NAMES: Record<string, string> = {
    kitchenknife: "Кухонний ніж", stoneknife: "Кам'яний ніж", policebaton: "Кийок", ak_bayonet: "Багнет АК",
};

const fmt = (n: number) => Math.round(n).toLocaleString("uk-UA");
// Закінчення за числом: 1 вбивство, 2 вбивства, 5 вбивств (11–14 — завжди «вбивств»)
const plural = (n: number, one: string, few: string, many: string) => {
    const v = Math.abs(Math.round(n)) % 100;
    if (v >= 11 && v <= 14) return many;
    if (v % 10 === 1) return one;
    if (v % 10 >= 2 && v % 10 <= 4) return few;
    return many;
};
const kills = (n: number) => plural(n, "вбивство", "вбивства", "вбивств");
const hits = (n: number) => plural(n, "влучання", "влучання", "влучань");
const pct = (part: number, total: number) => total > 0 ? `${((part / total) * 100).toFixed(1)}%` : "—";
const meters = (m: number) => m >= 1000 ? `${(m / 1000).toFixed(1)} км` : `${Math.round(m)} м`;
const weaponLabel = (name: string) => WEAPON_NAMES[name] ?? name.replace(/^as_/, "").replace(/_/g, " ").toUpperCase();
const zoneSum = (zones: Record<string, number>, keys: string[]) => keys.reduce((a, k) => a + (zones[k] ?? 0), 0);

const icon = (children: ReactNode) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {children}
    </svg>
);
// заражений із DayZ: іде, витягнувши руки вперед
const ZombieIcon = () => icon(<><circle cx="8.5" cy="5" r="2.2" /><path d="M9.5 8 8 14.5 6 21" /><path d="M8 14.5l3.5 2.5 1 4" /><path d="M9.2 9.5 15 9l3.5 1.5" /><path d="M8.8 12 14 11.5l3 1.5" /></>);
const PawIcon = () => icon(<><circle cx="6" cy="10" r="2" /><circle cx="10" cy="5.5" r="2" /><circle cx="14" cy="5.5" r="2" /><circle cx="18" cy="10" r="2" /><path d="M8 18c0-3 2-6 4-6s4 3 4 6c0 2-2 2.5-4 1.5C10 20.5 8 20 8 18z" /></>);
const PersonIcon = () => icon(<><circle cx="12" cy="8" r="4" /><path d="M4 20.5a8 8 0 0116 0" /></>);
const BootIcon = () => icon(<><path d="M7 3h5v9l7 3v4H5V3h2" /><path d="M5 19v2h14v-2" /></>);

const HeroStat = ({ icon, value, label }: { icon: ReactNode, value: string, label: string }) => (
    <div className="st-hstat">
        <span className="st-hstat-icon">{icon}</span>
        <b>{value}</b>
        <span>{label}</span>
    </div>
);

// Півколо влучності: частка влучних пострілів від усіх
const Gauge = ({ value }: { value: number }) => (
    <svg className="st-gauge" viewBox="0 0 120 70" aria-hidden="true">
        <path d="M10 62 A50 50 0 0 1 110 62" pathLength="100" className="st-gauge-track" />
        <path d="M10 62 A50 50 0 0 1 110 62" pathLength="100" className="st-gauge-bar" strokeDasharray={`${Math.min(100, value * 100)} 100`} />
    </svg>
);

const Ring = ({ value }: { value: number }) => (
    <svg className="st-ring" viewBox="0 0 64 64" aria-hidden="true">
        <circle cx="32" cy="32" r="26" pathLength="100" className="st-ring-track" />
        <circle cx="32" cy="32" r="26" pathLength="100" className="st-ring-bar" strokeDasharray={`${Math.min(100, value * 100)} 100`} />
    </svg>
);

// Розподіл влучань за ціллю; кольори перевірено на дальтонізм, порядок фіксований
const TARGETS = [
    { key: "hitPlayers", label: "Гравці", color: "#e5484d" },
    { key: "hitVehicles", label: "Техніка", color: "#3579f6" },
    { key: "hitInfected", label: "Зомбі", color: "#2f9e6a" },
    { key: "hitAnimals", label: "Тварини", color: "#c08a1a" },
] as const;

const ServerStats = ({ stats, serverName }: { stats: IServerStats, serverName: string }) => {
    const [active, setActive] = useState<ZoneId | null>(null);

    const counted = ZONES.map((z) => ({ ...z, count: zoneSum(stats.zones, z.keys) }));
    const totalZones = counted.reduce((a, z) => a + z.count, 0);
    const zones: SurvivorZone[] = counted.map((z) => ({ ...z, share: totalZones ? z.count / totalZones : 0 }));
    const sorted = zones.filter((z) => z.count > 0).sort((a, b) => b.count - a.count);
    const headShare = totalZones ? zoneSum(stats.zones, ["head", "brain"]) / totalZones : 0;


    const accuracy = stats.shots.fired > 0 ? stats.shots.hit / stats.shots.fired : 0;
    const targetTotal = TARGETS.reduce((a, t) => a + stats.shots[t.key], 0);

    return (
        <>
            <section className="st-hero">
                <div className="st-hero-info">
                    <div className="st-hero-head">
                        <div className="bc-icon st-hero-icon" aria-hidden="true"><PersonIcon /></div>
                        <div className="st-hero-title">
                            <span className="st-eyebrow">Сервер {serverName}</span>
                            <h2 className="st-name" title={stats.name}>{stats.name || "Гравець"}</h2>
                        </div>
                    </div>

                    <div className="st-kd">
                        <b>{stats.kdratio.toFixed(2)}</b>
                        <span className="st-kd-label">K/D</span>
                        <div className="st-kd-parts">
                            <span><b>{fmt(stats.kills.players)}</b> {kills(stats.kills.players)}</span>
                            <span><b>{fmt(stats.deaths)}</b> {plural(stats.deaths, "смерть", "смерті", "смертей")}</span>
                        </div>
                    </div>

                    <div className="st-hstats">
                        <HeroStat icon={<RatioIcon />} value={fmt(stats.kills.players)} label={`${kills(stats.kills.players)} усього`} />
                        <HeroStat icon={<SkullIcon />} value={fmt(stats.suicides)} label={plural(stats.suicides, "самогубство", "самогубства", "самогубств")} />
                        <HeroStat icon={<DistanceIcon />} value={meters(stats.longestKill)} label="найдальше вбивство" />
                        <HeroStat icon={<ZombieIcon />} value={fmt(stats.kills.infected)} label="вбито зомбі" />
                        <HeroStat icon={<PawIcon />} value={fmt(stats.kills.animals)} label={`вбито ${plural(stats.kills.animals, "тварину", "тварини", "тварин")}`} />
                        <HeroStat icon={<BootIcon />} value={meters(stats.distanceTraveled)} label="пройдено" />
                    </div>
                </div>

                <div className="st-hero-figure">
                    <Survivor zones={zones} active={active} onActive={setActive} />
                    <span className="st-hero-caption">
                        { totalZones ? `Куди ви влучаєте · ${fmt(totalZones)} ${hits(totalZones)} по гравцях` : "Влучань по гравцях поки немає" }
                    </span>
                </div>
            </section>

            <div className="st-grid">
                <section className="st-card">
                    <div className="st-card-head">
                        <h3>Влучання по тілу</h3>
                        <div className="st-headshot">
                            <Ring value={headShare} />
                            <span><b>{pct(headShare, 1)}</b>у голову</span>
                        </div>
                    </div>
                    { sorted.length === 0
                        ? <p className="st-muted">Влучань по гравцях поки немає.</p>
                        : <ul className="st-zone-list">
                            { sorted.map((z) =>
                                <li
                                    key={z.id}
                                    className={active === z.id ? "on" : ""}
                                    style={{ "--share": z.count / sorted[0].count } as CSSProperties}
                                    onMouseEnter={() => setActive(z.id)}
                                    onMouseLeave={() => setActive(null)}
                                >
                                    <span className="st-zone-name">{z.label}</span>
                                    <span className="st-zone-bar" aria-hidden="true"><i /></span>
                                    <span className="st-zone-count">{fmt(z.count)} <small>{pct(z.share, 1)}</small></span>
                                </li>
                            )}
                        </ul>
                    }
                </section>

                <section className="st-card">
                    <div className="st-card-head"><h3>Стрільба</h3></div>
                    <div className="st-accuracy">
                        <div className="st-gauge-wrap">
                            <Gauge value={accuracy} />
                            <div className="st-gauge-value"><b>{pct(stats.shots.hit, stats.shots.fired)}</b><span>влучність</span></div>
                        </div>
                        <dl className="st-shot-facts">
                            <div><dt>Вистрілів</dt><dd>{fmt(stats.shots.fired)}</dd></div>
                            <div><dt>Влучних</dt><dd>{fmt(stats.shots.hit)}</dd></div>
                            <div><dt>Найдальший постріл</dt><dd>{meters(stats.longestShot)}</dd></div>
                            <div><dt>Влучань по гравцях</dt><dd>{fmt(stats.shots.hitPlayers)}</dd></div>
                        </dl>
                    </div>

                    { targetTotal > 0 &&
                        <div className="st-targets">
                            <span className="st-label">По кому влучали</span>
                            <div className="st-stack" role="img" aria-label="Розподіл влучань за ціллю">
                                { TARGETS.filter((t) => stats.shots[t.key] > 0).map((t) =>
                                    <i
                                        key={t.key}
                                        title={`${t.label}: ${fmt(stats.shots[t.key])} (${pct(stats.shots[t.key], targetTotal)})`}
                                        style={{ flexGrow: stats.shots[t.key], backgroundColor: t.color }}
                                    />
                                )}
                            </div>
                            <ul className="st-legend">
                                { TARGETS.map((t) =>
                                    <li key={t.key}>
                                        <i style={{ backgroundColor: t.color }} />
                                        {t.label} <b>{fmt(stats.shots[t.key])}</b> <small>{pct(stats.shots[t.key], targetTotal)}</small>
                                    </li>
                                )}
                            </ul>
                        </div>
                    }
                </section>
            </div>

            { stats.weapons.length > 0 &&
                <section className="st-card">
                    <div className="st-card-head"><h3>Арсенал</h3><span className="st-muted">топ за вбивствами</span></div>
                    <ol className="st-weapons">
                        { stats.weapons.map((w, i) => {
                            const total = Object.values(w.zones).reduce((a, b) => a + b, 0);
                            // частка від усіх вбивств гравців (вбивства зброї в CFTools — саме по гравцях)
                            const share = stats.kills.players > 0 ? Math.min(1, w.kills / stats.kills.players) : 0;
                            return (
                                <li className="st-weapon" key={w.name} style={{ "--share": share } as CSSProperties}>
                                    <span className="st-weapon-rank">{i + 1}</span>
                                    <div className="st-weapon-main">
                                        <div className="st-weapon-top">
                                            <b>{weaponLabel(w.name)}</b>
                                            <span>{fmt(w.kills)} {kills(w.kills)}</span>
                                        </div>
                                        <div className="st-weapon-share">
                                            <span className="st-weapon-bar" aria-hidden="true"><i /></span>
                                            <small><b>{Math.round(share * 100)}%</b> усіх ваших вбивств</small>
                                        </div>
                                        <div className="st-chips">
                                            <span>{fmt(w.hits)} {hits(w.hits)}</span>
                                            <span>{fmt(w.damage)} шкоди</span>
                                            <span>голова {pct(zoneSum(w.zones, ["head", "brain"]), total)}</span>
                                            { w.longestKill > 0 && <span>до {meters(w.longestKill)}</span> }
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ol>
                </section>
            }
        </>
    );
};

// Порожній стан: напівпрозорий персонаж, пояснення і що з'явиться після гри
const EmptyStats = ({ title, text, error = false }: { title: string, text: string, error?: boolean }) => (
    <section className={`st-empty ${error ? "is-error" : ""}`}>
        <img className="st-empty-figure" src={survivorImg} alt="" />
        <div className="st-empty-info">
            <div className="bc-icon st-empty-icon" aria-hidden="true">{ error ? <ClockIcon /> : <PersonIcon /> }</div>
            <h3 className="bc-title">{title}</h3>
            <p className="bc-text">{text}</p>
            { !error &&
                <ul className="st-empty-list">
                    <li><RatioIcon /> K/D, вбивства і смерті</li>
                    <li><DistanceIcon /> Куди ви влучаєте по тілу</li>
                    <li><SkullIcon /> Влучність і улюблена зброя</li>
                </ul>
            }
        </div>
    </section>
);

// Особиста статистика гравця з CFTools: вбивства, постріли, влучання по частинах тіла, зброя
const Stats = () => {
    const [servers, setServers] = useState<IServer[] | null>(null);
    const [active, setActive] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        axios.get(PATHS_API.STATS_ME, { withCredentials: true })
            .then(({ data }) => {
                const list: IServer[] = data.servers ?? [];
                setServers(list);
                setActive((list.find((s) => s.stats) ?? list[0])?.key ?? "");
            })
            .catch((err) => setError(
                err.response?.status === 429
                    ? "Забагато запитів. Спробуйте за хвилину."
                    : "Статистика тимчасово недоступна. Спробуйте пізніше."
            ));
    }, []);

    if (error) return <EmptyStats error title="Статистика недоступна" text={error} />;
    if (!servers) return <div className="st-loading"><PageLoader size="md" /></div>;

    const current = servers.find((s) => s.key === active);

    return (
        <div className="st-page">
            { servers.length > 1 &&
                <div className="st-tabs" role="tablist">
                    { servers.map((s) =>
                        <button
                            key={s.key}
                            type="button"
                            role="tab"
                            aria-selected={s.key === active}
                            className={`st-tab ${s.key === active ? "on" : ""}`}
                            onClick={() => setActive(s.key)}
                        >
                            {s.name}
                        </button>
                    )}
                </div>
            }

            { current?.stats
                ? <ServerStats key={current.key} stats={current.stats} serverName={current.name} />
                : current?.unavailable
                    ? <EmptyStats error title="Не вдалося завантажити" text="CFTools зараз не відповідає. Спробуйте за хвилину." />
                    : <EmptyStats
                        title={`На ${current?.name ?? "цьому сервері"} ще порожньо`}
                        text="Зайдіть на сервер і зіграйте: щойно з'являться перші вбивства чи постріли, статистика буде тут."
                    />
            }
            <p className="st-note">Дані оновлюються приблизно раз на 5 хвилин.</p>
        </div>
    );
};

export default Stats;
